/**
 * dashboardCongTy — cho MỘT dashboard hiện dữ liệu của NHIỀU công ty.
 *
 * Bài toán: chủ một nhóm công ty (hoặc kế toán làm cho nhiều công ty) muốn mở một dashboard là thấy
 * doanh thu / công nợ / số đơn của công ty A, B, C cạnh nhau — không phải đổi công ty ba lần.
 *
 * Cách làm: mỗi widget được khai CÔNG TY LẤY DỮ LIỆU trong `widgetConfig.id_apps`:
 *
 *   "widgetConfig": { "apiCode": "dh2", "id_apps": ["_hien_tai", "64f0…a1", "64f0…b2"] }
 *
 *   - Không khai / mảng rỗng      -> công ty đang đăng nhập (HÀNH VI CŨ, không đổi gì).
 *   - Một công ty                 -> widget hiện dữ liệu của công ty đó, kèm nhãn tên công ty.
 *   - Nhiều công ty:
 *       · counter                 -> CỘNG TỔNG số của các công ty, rê chuột xem từng công ty.
 *       · list/report/pivot/custom-> thanh chọn công ty ngay trong widget (mỗi lần xem một công ty).
 *   - "_hien_tai"                 -> luôn là công ty đang đăng nhập (dashboard dùng chung cho nhiều
 *                                    công ty mà vẫn muốn có "công ty của tôi" bên cạnh công ty mẹ).
 *
 * KHÔNG phải sửa từng loại widget: widget chạy trong một AuthContext "đội lốt" công ty khác —
 * `config.id_app`, `config.get('id_app')`, `appInfo` và `apis` (mọi hàm của APIs.js đọc
 * `this.config.get("id_app")`) đều trả về công ty của widget. Trạng thái dùng chung của APIs (token,
 * phiên, cache, interceptor) vẫn là MỘT — Proxy chỉ thay `config`, mọi phép GHI đều rơi vào đối tượng
 * thật. Máy chủ vẫn gác như cũ: `/api/<id_app>/…` kiểm phạm vi công ty của token (isAppAllowed) và
 * quyền của người dùng ở công ty đó — dashboard KHÔNG mở thêm quyền nào.
 *
 * Cấu hình của widget (pivotconfig …) là bản ghi của CÔNG TY SỞ HỮU DASHBOARD, nên phải đọc bằng
 * ngữ cảnh gốc: `nguCanhCauHinh(context)`.
 *
 * File KHÔNG import gì để test node chạy thẳng trên mã nguồn (xem
 * fontend/flexbiz-api/scripts/dashboard-nhieu-cong-ty-test.mjs).
 */

/** Mã đại diện "công ty đang đăng nhập" trong `id_apps`. */
export const MA_CONG_TY_HIEN_TAI = '_hien_tai';

/**
 * Loại widget dùng được với công ty khác. Còn lại KHÔNG:
 *   - shortcut / list(action:add): đi tới trang / mở form của công ty ĐANG ĐĂNG NHẬP — gắn công ty khác
 *     là mở nhầm chỗ, ghi nhầm công ty.
 *   - workflow / flow: là sơ đồ (bản ghi của công ty sở hữu dashboard), không phải số liệu.
 */
export const LOAI_HO_TRO_NHIEU_CONG_TY = ['counter', 'list', 'report', 'pivot', 'custom'];

export const hoTroNhieuCongTy = (widget) => {
	if (!widget || !LOAI_HO_TRO_NHIEU_CONG_TY.includes(widget.type)) return false;
	const cfg = widget.widgetConfig || {};
	if (widget.type === 'list' && cfg.action === 'add') return false;
	return true;
};

/**
 * Danh sách công ty THẬT của widget (đã thay `_hien_tai`, bỏ rỗng, bỏ trùng, giữ thứ tự khai).
 * Trả [] khi widget không khai -> dùng công ty đang đăng nhập như cũ.
 * Widget không hỗ trợ (shortcut…) thì luôn [] dù có khai — không âm thầm đổi chỗ ghi dữ liệu.
 */
export const dsCongTyCuaWidget = (widget, idHienTai) => {
	if (!hoTroNhieuCongTy(widget)) return [];
	const raw = (widget.widgetConfig || {}).id_apps;
	const ds = Array.isArray(raw) ? raw : (typeof raw === 'string' && raw.trim() ? [raw] : []);
	const kq = [];
	ds.forEach((x) => {
		let id = String(x == null ? '' : x).trim();
		if (id === MA_CONG_TY_HIEN_TAI) id = idHienTai ? String(idHienTai) : '';
		if (id && !kq.includes(id)) kq.push(id);
	});
	return kq;
};

/** Widget có cần chạy ngoài công ty đang đăng nhập không (đúng 1 công ty = công ty hiện tại thì KHÔNG). */
export const canPhamViCongTy = (widget, idHienTai) => {
	const ds = dsCongTyCuaWidget(widget, idHienTai);
	if (ds.length === 0) return false;
	return !(ds.length === 1 && ds[0] === String(idHienTai || ''));
};

/** Cộng số của từng công ty; công ty chưa có số (đang tải / lỗi) thì chưa cộng. */
export const tongSoCongTy = (soTheoCongTy) => {
	let tong = 0;
	let du = 0;
	Object.keys(soTheoCongTy || {}).forEach((k) => {
		const v = Number(soTheoCongTy[k]);
		if (Number.isFinite(v)) { tong += v; du += 1; }
	});
	return { tong, soCongTyDaCo: du };
};

/** Tìm hàm trên CHUỖI PROTOTYPE (không tính Object.prototype) — bản gốc chưa bind của APIs. */
const timHamPrototype = (obj, prop) => {
	let p = Object.getPrototypeOf(obj);
	while (p && p !== Object.prototype) {
		const d = Object.getOwnPropertyDescriptor(p, prop);
		if (d) return typeof d.value === 'function' ? d.value : null;
		p = Object.getPrototypeOf(p);
	}
	return null;
};

/**
 * Bọc object cấu hình: chỉ `id_app` (thuộc tính lẫn `get('id_app')`) đổi sang công ty của widget.
 * Widget KHÔNG được đổi công ty toàn cục: ghi `id_app` qua bản bọc bị bỏ qua.
 */
export const bocCauHinh = (cauHinhGoc, id_app) => {
	const goc = cauHinhGoc || {};
	const get = (name) => {
		if (name === 'id_app') return id_app;
		return typeof goc.get === 'function' ? goc.get(name) : goc[name];
	};
	return new Proxy(goc, {
		get(t, p) {
			if (p === 'id_app') return id_app;
			if (p === 'get') return get;
			if (p === '__cauHinhGoc') return t;
			return Reflect.get(t, p);
		},
		set(t, p, v) {
			if (p === 'id_app') return true;
			t[p] = v;
			return true;
		},
	});
};

/**
 * Bọc đối tượng APIs: `this.config` bên trong mọi hàm của prototype trỏ sang cấu hình đã bọc.
 *
 * APIs.js `bind` sẵn từng hàm trong constructor (`this.asyncGetList = this.asyncGetList.bind(this)`), nên
 * chỉ thay `config` là KHÔNG đủ — hàm đã bind vẫn thấy `this` là đối tượng thật. Proxy trả về bản
 * PROTOTYPE bind với chính Proxy -> `this.config` là bản bọc, còn mọi thuộc tính khác (token, phiên,
 * cache, axios…) đọc/ghi thẳng vào đối tượng thật.
 */
export const bocApis = (apisGoc, cauHinh) => {
	if (!apisGoc) return apisGoc;
	const daBind = new Map();
	const proxy = new Proxy(apisGoc, {
		get(t, p, receiver) {
			if (p === 'config') return cauHinh;
			if (p === '__apisGoc') return t;
			if (typeof p === 'string' && p !== 'constructor') {
				const fn = timHamPrototype(t, p);
				if (fn) {
					if (!daBind.has(p)) daBind.set(p, fn.bind(receiver));
					return daBind.get(p);
				}
			}
			return Reflect.get(t, p);
		},
		set(t, p, v) {
			if (p === 'config') return true;
			t[p] = v;
			return true;
		},
	});
	return proxy;
};

/**
 * Ngữ cảnh (AuthContext) "đội lốt" công ty `appInfo._id`. Giữ `nguCanhGoc` để đọc cấu hình của
 * công ty sở hữu dashboard và để biết widget đang chạy ngoài công ty hiện tại.
 */
export const taoNguCanhCongTy = (context, appInfo) => {
	const id_app = appInfo && appInfo._id ? String(appInfo._id) : '';
	if (!context || !id_app) return context;
	const goc = context.nguCanhGoc || context;
	const cauHinh = bocCauHinh(goc.config, id_app);
	const apis = bocApis(goc.apis, goc.apis && goc.apis.config === goc.config ? cauHinh : bocCauHinh(goc.apis && goc.apis.config, id_app));
	return {
		...goc,
		config: cauHinh,
		apis,
		appInfo,
		nguCanhGoc: goc,
		congTyWidget: { id_app, name: appInfo.name || id_app },
	};
};

/** Ngữ cảnh để đọc CẤU HÌNH widget (pivotconfig…) — luôn là công ty sở hữu dashboard. */
export const nguCanhCauHinh = (context) => (context && context.nguCanhGoc) || context;

// ───────────────────────── BỘ LỌC CÔNG TY CHUNG Ở ĐẦU DASHBOARD ─────────────────────────
//
// Một lựa chọn cho CẢ dashboard thay vì chỉnh từng widget. Quy ước:
//   - `null` = mặc định "công ty đang đăng nhập": mỗi widget theo cấu hình riêng của nó (không khai
//     id_apps thì là công ty đang đăng nhập — tức đúng như trước khi có bộ lọc).
//   - mảng id = MỌI widget hỗ trợ (counter/list/report/pivot/custom) lấy dữ liệu của đúng các công ty
//     này, ĐÈ lên id_apps riêng của widget. Loại không hỗ trợ (lối tắt, nút thêm mới, flow…) đứng yên.

/**
 * Chuẩn hoá lựa chọn của bộ lọc. Trả `null` (= mặc định) khi rỗng hoặc chỉ có công ty đang đăng nhập.
 * `dsHopLe` (tuỳ chọn): các công ty người dùng còn được vào — lựa chọn đã lưu mà nay mất quyền thì bỏ.
 * Truyền `null` khi danh sách chưa tải xong để không xoá nhầm lựa chọn đã lưu.
 */
export const chuanHoaLocCongTy = (ds, idHienTai, dsHopLe = null) => {
	if (!Array.isArray(ds)) return null;
	const hienTai = idHienTai ? String(idHienTai) : '';
	const hopLe = Array.isArray(dsHopLe) ? new Set(dsHopLe.map(String)) : null;
	const kq = [];
	ds.forEach((x) => {
		let id = String(x == null ? '' : x).trim();
		if (id === MA_CONG_TY_HIEN_TAI) id = hienTai;
		if (!id) return;
		if (hopLe && id !== hienTai && !hopLe.has(id)) return;
		if (!kq.includes(id)) kq.push(id);
	});
	if (kq.length === 0) return null;
	if (kq.length === 1 && kq[0] === hienTai) return null;
	return kq;
};

/** Công ty widget thật sự dùng: bộ lọc chung (nếu đang chọn) thắng cấu hình riêng của widget. */
export const dsCongTyHieuLuc = (widget, idHienTai, locCongTy) => {
	if (!hoTroNhieuCongTy(widget)) return [];
	if (Array.isArray(locCongTy) && locCongTy.length) return locCongTy.map(String);
	return dsCongTyCuaWidget(widget, idHienTai);
};

export const canPhamViCongTyHieuLuc = (widget, idHienTai, locCongTy) => {
	const ds = dsCongTyHieuLuc(widget, idHienTai, locCongTy);
	if (ds.length === 0) return false;
	return !(ds.length === 1 && ds[0] === String(idHienTai || ''));
};

/** Khoá lưu lựa chọn của bộ lọc (theo người xem, công ty đang đăng nhập và mã dashboard). */
export const khoaLuuLocCongTy = (idHienTai, maDashboard) => `flexbiz_dash_loc_cong_ty:${idHienTai || ''}:${maDashboard || ''}`;

/**
 * Có KHOÁ chuyển trang chi tiết không. Trang /page/... luôn mở theo công ty đang đăng nhập, nên chỉ khoá
 * khi số liệu widget đang hiện KHÁC công ty đó:
 *   - thẻ đếm cộng tổng nhiều công ty (`laTong`)  -> khoá (con số không thuộc riêng công ty nào);
 *   - đang xem một công ty (kể cả qua thanh chọn của widget nhiều công ty) -> khoá khi và chỉ khi
 *     công ty đó khác công ty đang đăng nhập.
 * Lỗi cũ (22/10/2026): widget nhiều công ty khoá CỨNG cho mọi lựa chọn trên thanh — đang xem đúng công
 * ty đang đăng nhập vẫn báo "Đổi sang công ty đó để mở trang chi tiết".
 */
export const khoaChuyenTrang = (idDangXem, idHienTai, laTong = false) => {
	if (laTong) return true;
	if (!idDangXem) return false;
	return String(idDangXem) !== String(idHienTai || '');
};
